import { HexColorPicker } from 'react-colorful'
import { useState } from 'react'
import { useBuilder } from '../../context/BuilderContext'

export default function BodySettings() {
  const { bodySettings, updateBodySettings } = useBuilder()
  const [showBgPicker, setShowBgPicker] = useState(false)
  const [showContentBgPicker, setShowContentBgPicker] = useState(false)
  const [showLinkPicker, setShowLinkPicker] = useState(false)

  const settings = bodySettings || {}

  const update = (updates) => {
    updateBodySettings(updates)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Email Body Settings</h4>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
        These settings apply to the overall email layout
      </p>

      {/* Preheader Text */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Preheader Text
        </label>
        <textarea
          value={settings.preheaderText || ''}
          onChange={(e) => update({ preheaderText: e.target.value })}
          placeholder="Preview text shown in inbox before opening..."
          rows={2}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.5rem', fontFamily: 'inherit', resize: 'vertical' }}
        />
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.65rem', color: '#94a3b8' }}>
          Hidden text shown as preview in email clients (40-130 chars recommended)
        </p>
        {settings.preheaderText && (
          <div style={{
            fontSize: '0.65rem',
            color: settings.preheaderText.length > 130 ? '#dc2626' : settings.preheaderText.length < 40 ? '#f59e0b' : '#22c55e',
            marginTop: '0.25rem'
          }}>
            {settings.preheaderText.length} / 130 characters
          </div>
        )}
      </div>

      {/* Email Width */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Email Width: {settings.emailWidth || 600}px
        </label>
        <input
          type="range"
          min="400"
          max="800"
          step="20"
          value={parseInt(settings.emailWidth) || 600}
          onChange={(e) => update({ emailWidth: parseInt(e.target.value) })}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
          <span>400px</span>
          <span>600px</span>
          <span>800px</span>
        </div>
      </div>

      {/* Background Color (outer) */}
      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Page Background
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowBgPicker(!showBgPicker)}
            style={{
              width: '40px',
              height: '32px',
              backgroundColor: settings.backgroundColor || '#f4f4f4',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          />
          <input
            type="text"
            value={settings.backgroundColor || '#f4f4f4'}
            onChange={(e) => update({ backgroundColor: e.target.value })}
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showBgPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div style={{ position: 'fixed', inset: 0 }} onClick={() => setShowBgPicker(false)} />
            <HexColorPicker
              color={settings.backgroundColor || '#f4f4f4'}
              onChange={(color) => update({ backgroundColor: color })}
            />
          </div>
        )}
      </div>

      {/* Content Background Color */}
      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Content Background
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowContentBgPicker(!showContentBgPicker)}
            style={{
              width: '40px',
              height: '32px',
              backgroundColor: settings.contentBackgroundColor || '#ffffff',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          />
          <input
            type="text"
            value={settings.contentBackgroundColor || '#ffffff'}
            onChange={(e) => update({ contentBackgroundColor: e.target.value })}
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showContentBgPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div style={{ position: 'fixed', inset: 0 }} onClick={() => setShowContentBgPicker(false)} />
            <HexColorPicker
              color={settings.contentBackgroundColor || '#ffffff'}
              onChange={(color) => update({ contentBackgroundColor: color })}
            />
          </div>
        )}
      </div>

      {/* Default Font */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Default Font
        </label>
        <select
          value={settings.fontFamily || 'Arial, sans-serif'}
          onChange={(e) => update({ fontFamily: e.target.value })}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        >
          <option value="Arial, sans-serif">Arial</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', serif">Times New Roman</option>
          <option value="'Courier New', monospace">Courier New</option>
          <option value="Verdana, sans-serif">Verdana</option>
          <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
          <option value="Tahoma, sans-serif">Tahoma</option>
          <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
        </select>
      </div>

      {/* Default Link Color */}
      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Link Color
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowLinkPicker(!showLinkPicker)}
            style={{
              width: '40px',
              height: '32px',
              backgroundColor: settings.linkColor || '#007bff',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          />
          <input
            type="text"
            value={settings.linkColor || '#007bff'}
            onChange={(e) => update({ linkColor: e.target.value })}
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showLinkPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div style={{ position: 'fixed', inset: 0 }} onClick={() => setShowLinkPicker(false)} />
            <HexColorPicker
              color={settings.linkColor || '#007bff'}
              onChange={(color) => update({ linkColor: color })}
            />
          </div>
        )}
      </div>

      {/* Content Padding */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Content Padding: {settings.contentPadding || 0}%
        </label>
        <input
          type="range"
          min="0"
          max="10"
          step="1"
          value={parseInt(settings.contentPadding) || 0}
          onChange={(e) => update({ contentPadding: parseInt(e.target.value) })}
          style={{ width: '100%', cursor: 'pointer' }}
        />
      </div>

      {/* Border Radius */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Content Border Radius
        </label>
        <select
          value={settings.contentBorderRadius || '0px'}
          onChange={(e) => update({ contentBorderRadius: e.target.value })}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        >
          <option value="0px">None</option>
          <option value="4px">Small (4px)</option>
          <option value="8px">Medium (8px)</option>
          <option value="12px">Large (12px)</option>
          <option value="16px">Extra Large (16px)</option>
        </select>
      </div>
    </div>
  )
}
